import React, { useState } from "react";
import { Box } from "@mui/material";
import { InputField } from "../../../shared/inputs/InputField.styled";
import { HeaderText } from "../../../shared/text/HeaderText.styled";
import TakeOffLoginShell from "../shared/TakeOffLoginShell";
import { useBanner } from "../../../context/BannerContext";
import { track } from "../../../../clients/mixpanel";
import { useTakeOffLoginActions } from "../../../hooks/useTakeOffLoginActions";
import { useTogal } from "./TogalContext";
import { TOGAL_LOGIN_USERNAME_KEY } from "../../../../integrations/qto/sources/togal/api";
import { TOGAL_LOGIN_ERROR, TOGAL_SIGN_IN_LABEL } from "../../../constants";
import { browserStore } from "../../../../utils/storageKeys";

const INVALID_EMAIL_MESSAGE = "Enter a valid email address";
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = (value: string): boolean => EMAIL_PATTERN.test(value.trim());

/** Togal signs in with email and password against its own API. */
const TogalLoginForm: React.FC = () => {
  const { showBanner } = useBanner();
  const { initializeTogalState, setTogalState } = useTogal();
  const { handleTogalLogin } = useTakeOffLoginActions();

  const [email, setEmail] = useState<string>(() => browserStore.get(TOGAL_LOGIN_USERNAME_KEY) ?? "");
  const [password, setPassword] = useState<string>("");
  const [emailError, setEmailError] = useState<boolean>(false);
  const [loginError, setLoginError] = useState<boolean>(false);
  const [busy, setBusy] = useState<boolean>(false);

  const handleSignIn = async () => {
    if (!isValidEmail(email)) {
      setEmailError(true);
      return;
    }

    setLoginError(false);
    setBusy(true);
    const loggedIn = await handleTogalLogin(email, password);
    setBusy(false);

    if (!loggedIn) {
      setLoginError(true);
      // Deliberately generic: rejected credentials do not come back from Togal as a credential
      // error we can identify, so a wrong password and an unreachable API are indistinguishable
      // here. Naming one of them would be wrong half the time.
      showBanner(TOGAL_LOGIN_ERROR, "error");
      return;
    }

    browserStore.set(TOGAL_LOGIN_USERNAME_KEY, email.trim());
    void track("excel.qto.togal.login");
    setTogalState("selector");
    await initializeTogalState();
  };

  return (
    <TakeOffLoginShell
      sourceId="togal"
      submitLabel={TOGAL_SIGN_IN_LABEL}
      busy={busy}
      submitDisabled={busy || !email || !password}
      onSubmit={() => void handleSignIn()}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: "16px" }}>
        <Box>
          <HeaderText sx={{ pb: "4px", fontSize: "12px" }}>Email</HeaderText>
          <InputField
            fullWidth
            size="small"
            variant="outlined"
            type="email"
            required
            error={emailError || loginError}
            helperText={emailError ? INVALID_EMAIL_MESSAGE : undefined}
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              setEmailError(false);
              setLoginError(false);
            }}
            onBlur={() => setEmailError(email.trim().length > 0 && !isValidEmail(email))}
          />
        </Box>
        <Box>
          <HeaderText sx={{ pb: "4px", fontSize: "12px" }}>Password</HeaderText>
          <InputField
            fullWidth
            size="small"
            variant="outlined"
            type="password"
            required
            error={loginError}
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
              setLoginError(false);
            }}
          />
        </Box>
      </Box>
    </TakeOffLoginShell>
  );
};

export default TogalLoginForm;
